/** Response shapes returned by the API (the web app consumes the same types). */
import type { UserStatus, UserType } from './api';
import type { BdsUserRef } from './dossier-dto';
import type { FileArea, FileStatus } from './files';
import type { NotificationType } from './notifications';
import type { AppModule, PermissionAction } from './permissions';
import type { Role } from './roles';

export const EXPORTER_STATUSES = ['active', 'inactive', 'suspended'] as const;
export type ExporterStatus = (typeof EXPORTER_STATUSES)[number];

export interface UserDto {
  id: string;
  email: string;
  fullName: string;
  role: Role;
  roleLabel: string;
  userType: UserType;
  department: string | null;
  exporterId: string | null;
  exporterName: string | null;
  designation: string | null;
  phone: string | null;
  notes: string | null;
  status: UserStatus;
  hasPassword: boolean;
  mustChangePassword: boolean;
  locked: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  legacyId: string | null;
}

export interface UserLookupDto {
  id: string;
  fullName: string;
  email: string;
  role: Role;
  department: string | null;
  status: UserStatus;
}

export interface CreatedUserDto {
  user: UserDto;
  temporaryPassword: string | null;
}

export interface ExporterDto {
  id: string;
  code: string;
  serialNo: number | null;
  companyName: string;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  address: string | null;
  website: string | null;
  notes: string | null;
  status: ExporterStatus;
  /** SC-NBD user in BDS who looks after this client: its portal requisitions show to their BDS team. */
  bdContact: BdsUserRef | null;
  loginCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ExporterLookupDto {
  id: string;
  code: string;
  companyName: string;
  status: ExporterStatus;
}

export interface DepartmentDto {
  code: string;
  name: string;
  source: 'builtin' | 'custom';
  active: boolean;
}

export interface CountryDto {
  id: string;
  name: string;
}

export interface NotificationDto {
  id: string;
  type: NotificationType;
  title: string;
  body: string | null;
  link: string | null;
  entityType: string | null;
  entityId: string | null;
  actorName: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface ActivityLogDto {
  id: string;
  actorId: string | null;
  actorEmail: string;
  actorRole: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  summary: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  createdAt: string;
  legacy: boolean;
}

export interface FileRefDto {
  id: string;
  area: FileArea;
  name: string;
  contentType: string;
  sizeBytes: number | null;
  status: FileStatus;
  uploadedAt: string | null;
  uploadedByEmail: string | null;
}

export interface UploadTargetDto {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresAt: string;
}

export interface CreatedUploadDto {
  file: FileRefDto;
  upload: UploadTargetDto;
}

export interface DownloadUrlDto {
  url: string;
  expiresAt: string | null;
  /** True when the file still lives in Google Drive (imported, not yet copied to storage). */
  legacy: boolean;
}

export interface MatrixRowDto {
  role: Role;
  module: AppModule;
  view: boolean;
  edit: boolean;
  delete: boolean;
  export: boolean;
  source: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

export type CeilingSet = 'all' | 'staff' | readonly Role[];

export interface AccessMatrixDto {
  roles: { role: Role; label: string }[];
  modules: { module: AppModule; label: string }[];
  ceilings: Record<AppModule, Record<PermissionAction, CeilingSet>>;
  rows: MatrixRowDto[];
}

export interface SettingDto {
  key: string;
  value: unknown;
  description: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}
